import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { useCreateFarm, useCurrentFarm, useUserFarms } from "model";
import { ChangeEvent, FC, MutableRefObject, useEffect, useState } from "react";
import { MapRef } from "react-map-gl/mapbox";
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { fetchIPLocation, switchFarm } from "services";
import { useAppStore } from "stores";

import { envelope, lineString } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import {
  DrawerForm,
  FullScreenDialog,
  InfoSnackbar,
  MobileTopHintContainer,
  VerticalCenterContainer,
} from "common/components";
import PostcodeLookupInput from "common/components/PostcodeLookupInput";

interface FarmInputProps {
  mapRef: MutableRefObject<MapRef | null>;
}

export const FarmInput: FC<FarmInputProps> = ({ mapRef }) => {
  const [farmName, setFarmName] = useState<string>("");
  const [farmNameError, setFarmNameError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const user = useAppStore((state) => state.user);
  const userId = user?.guid;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const path = location.pathname.replace(/\/enter-name$|\/find-location$/, "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  // Use React Query hooks for farms
  const { data: userFarms } = useUserFarms();
  const { data: currentFarm } = useCurrentFarm();
  // Use React Query mutation for creating farms
  const createFarmMutation = useCreateFarm();

  useEffect(() => {
    // fetching ip data once on mount
    (async () => {
      await fetchIPLocation();
    })().catch((error) => {
      console.error("Failed to fetch IP location", error);
    });
  }, []);
  let currentFarmGuid = ""; // should be always set later when we have at least 1 farm
  if (userFarms.length > 0) {
    currentFarmGuid = currentFarm ? currentFarm.guid : userFarms[0].guid;
  }

  const handleSave = async (): Promise<void> => {
    if (farmName === "") {
      setFarmNameError(true);
      return;
    }

    setIsLoading(true);

    const map = mapRef.current?.getMap();
    const farmGeom = envelope(lineString(map.getBounds().toArray()));

    if (farmGeom.geometry && userId) {
      try {
        const newFarm = await createFarmMutation.mutateAsync({
          guid: "new",
          name: farmName,
          owner: userId,
          user: userId,
          geom: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {},
                geometry: {
                  type: "MultiPolygon",
                  coordinates: [farmGeom.geometry?.coordinates],
                },
              },
            ],
          },
          fields: [],
          animalGroups: [],
        });

        notify(t("farmInput.add.success", { farmName }));

        // TODO: figure out why newFarm can be undefined; or this check necessary?
        if (newFarm) {
          void switchFarm(newFarm.guid);
          mixpanel.track(AnalyticsEvents.FARM_CREATED);
          navigate(`/farm/${newFarm.guid}`, { replace: true });
        } else {
          navigate("");
        }
      } catch (err) {
        mixpanel.track(AnalyticsEvents.FARM_ADD_FAILED);
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (farmName !== "") {
      setFarmNameError(false);
    }

    setFarmName(e.target.value);
  };

  if (isMobile) {
    return (
      <>
        <Routes>
          <Route
            path="enter-name"
            element={
              <FullScreenDialog>
                <DialogTitle>{t("farm.add.addName.mobile")}</DialogTitle>
                <DialogContent>
                  <VerticalCenterContainer>
                    <Container>
                      <DrawerForm>
                        <TextField
                          variant="standard"
                          required
                          value={farmName}
                          error={farmNameError}
                          helperText={t("farm.add.addName.error")}
                          onChange={onInputChange}
                        />
                      </DrawerForm>
                    </Container>
                  </VerticalCenterContainer>
                </DialogContent>
                <DialogActions>
                  {userFarms.length > 0 && (
                    <Button
                      component={Link}
                      to={`/farm/${currentFarmGuid}`}
                      color="secondary"
                    >
                      {t("edit.button.cancel")}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    onClick={(e) => {
                      if (farmName === "") {
                        e.preventDefault();
                        setFarmNameError(true);
                      } else {
                        navigate(`${path}/find-location`);
                      }
                    }}
                  >
                    {t("edit.button.next")}
                  </Button>
                </DialogActions>
              </FullScreenDialog>
            }
          />
          <Route
            path="find-location"
            element={
              <Dialog
                open={true}
                fullScreen
                style={{
                  pointerEvents: "none",
                  background: "transparent",
                  backgroundColor: "transparent",
                }}
                BackdropProps={{
                  style: {
                    backgroundColor: "transparent",
                    background: "transparent",
                    pointerEvents: "none",
                  },
                }}
                hideBackdrop={true}
                sx={{
                  "& .MuiDialog-paper": {
                    backgroundColor: "transparent !important",
                    background: "transparent !important",
                  },
                  "& .MuiDialog-container": {
                    backgroundColor: "transparent !important",
                    background: "transparent !important",
                  },
                }}
                slotProps={{
                  paper: {
                    style: {
                      backgroundColor: "transparent",
                      background: "transparent",
                      boxShadow: "none",
                      pointerEvents: "none",
                      overflow: "visible",
                    },
                  },
                }}
              >
                <MobileTopHintContainer
                  style={{
                    pointerEvents: "auto",
                    background: "transparent",
                    backgroundColor: "transparent",
                  }}
                >
                  <PostcodeLookupInput />
                </MobileTopHintContainer>
                <InfoSnackbar
                  open={snackbarOpen}
                  handleClose={handleSnackbarClose}
                  message={t("farm.add.locationInstructions")}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "calc(64px + env(safe-area-inset-bottom))",
                    left: 0,
                    right: 0,
                    pointerEvents: "auto",
                    backgroundColor: "white",
                    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                    borderTop: "1px solid #e0e0e0",
                    padding: "16px",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      color="secondary"
                      onClick={() => navigate("enter-name")}
                    >
                      {t("edit.button.back")}
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      {t("edit.button.save")}
                    </Button>
                  </div>
                </div>
              </Dialog>
            }
          />
        </Routes>
      </>
    );
  } else {
    return (
      <>
        <Container style={{ whiteSpace: "normal", paddingTop: "20px" }}>
          <Typography
            variant="h6"
            sx={{
              marginBottom: "16px",
            }}
          >
            {t("farm.add.addName.desktop")}
          </Typography>
          <TextField
            variant="standard"
            required
            value={farmName}
            error={farmNameError}
            helperText={t("farm.add.addName.error")}
            onChange={onInputChange}
            fullWidth
            style={{ paddingBottom: "25px" }}
            color="primary"
          />
          <PostcodeLookupInput />
          <Typography
            variant="body1"
            sx={{
              marginBottom: "16px",
            }}
          >
            {t("farm.add.saveLocation.desktop")}
            <span role="img" aria-label="point right">
              👉
            </span>
            {t("farm.add.saveFarm.desktop")}
          </Typography>
        </Container>
        <Container>
          {userFarms.length > 0 && (
            <CancelButton
              onClick={() => navigate(`/farm/${currentFarmGuid}`)}
              color="secondary"
            >
              {t("edit.button.cancel")}
            </CancelButton>
          )}
          <SaveButton color="primary" onClick={handleSave} disabled={isLoading}>
            {t("edit.button.save")}
          </SaveButton>
        </Container>
      </>
    );
  }
  function handleSnackbarClose() {
    setSnackbarOpen(false);
  }
};

const SaveButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(3),
}));

const CancelButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(15),
}));
